import { formatBytes, formatDuration } from '@setone-sounds/shared/format';
import type { FileRecord, SearchResultItem } from '@setone-sounds/shared/types';

export type LibraryItem = FileRecord | (SearchResultItem & Partial<Pick<FileRecord, 'size' | 'durationMs' | 'extension' | 'parentPath'>>);

export function isAudioItem(item: { type: string }): boolean {
  return item.type === 'AUDIO';
}

export function isTextItem(item: { type: string }): boolean {
  return item.type === 'TEXT';
}

export function parentPathOf(relativePath: string): string {
  const idx = relativePath.lastIndexOf('/');
  return idx === -1 ? '' : relativePath.slice(0, idx);
}

export function formatSizeSafe(bytes: number | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '—';
  try {
    return formatBytes(bytes);
  } catch {
    return '—';
  }
}

export function formatDurationSafe(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  try {
    return formatDuration(ms);
  } catch {
    return '—';
  }
}

export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function extensionLabel(item: LibraryItem): string {
  if ('extension' in item && item.extension) {
    return item.extension.replace(/^\./, '').toUpperCase() || '—';
  }
  const dot = item.filename.lastIndexOf('.');
  if (dot === -1) return '—';
  return item.filename.slice(dot + 1).toUpperCase() || '—';
}

export function downloadUrl(fileId: string): string {
  return `/api/files/${fileId}/download`;
}

export function streamUrl(fileId: string): string {
  return `/api/files/${fileId}/stream`;
}

/** Split text for lexical highlight — returns plain segments only (React-escaped). */
export function splitHighlight(text: string, query: string): Array<{ text: string; match: boolean }> {
  const q = query.trim();
  if (!q) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const parts: Array<{ text: string; match: boolean }> = [];
  let start = 0;
  let idx = lower.indexOf(needle, start);
  while (idx !== -1) {
    if (idx > start) parts.push({ text: text.slice(start, idx), match: false });
    parts.push({ text: text.slice(idx, idx + needle.length), match: true });
    start = idx + needle.length;
    idx = lower.indexOf(needle, start);
  }
  if (start < text.length) parts.push({ text: text.slice(start), match: false });
  return parts.length ? parts : [{ text, match: false }];
}

export function pathSegments(path: string): string[] {
  if (!path) return [];
  return path.split('/').filter(Boolean);
}
